import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../shared/hooks/useTranslation';
import style from './LanguageSelector.module.scss';

const languages = [
  { value: 'ru', label: 'Русский', short: 'RU' },
  { value: 'kk', label: 'Қазақша', short: 'ҚАЗ' },
  { value: 'en', label: 'English', short: 'EN' },
] as const;

type Props = {
  className?: string;
  variant?: 'compact' | 'full';
};

export function LanguageSelector({ className, variant = 'compact' }: Props) {
  const { language, changeLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant !== 'compact') return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [variant]);

  if (variant === 'full') {
    return (
      <div className={`${style.fullSelector} ${className || ''}`}>
        <div className={style.fullLanguages}>
          {languages.map((lang) => (
            <button
              key={lang.value}
              className={`${style.fullButton} ${language === lang.value ? style.fullActive : ''}`}
              onClick={() => changeLanguage(lang.value as 'ru' | 'kk' | 'en')}
              type="button"
            >
              <span className={style.fullLabel}>{lang.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const current = languages.find((l) => l.value === language) || languages[0];

  return (
    <div className={`${style.wrapper} ${className || ''}`} ref={ref}>
      <button
        className={style.trigger}
        onClick={() => setOpen(!open)}
        type="button"
        aria-label="Change language"
      >
        <svg
          className={style.globeIcon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
        </svg>
        <span className={style.currentLang}>{current.short}</span>
        <svg
          className={`${style.chevron} ${open ? style.chevronOpen : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className={style.dropdown}>
          {languages.map((lang) => (
            <button
              key={lang.value}
              className={`${style.option} ${language === lang.value ? style.optionActive : ''}`}
              onClick={() => {
                changeLanguage(lang.value as 'ru' | 'kk' | 'en');
                setOpen(false);
              }}
              type="button"
            >
              <span className={style.optionLabel}>{lang.label}</span>
              <span className={style.optionShort}>{lang.short}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
